import styled, { css } from 'styled-components';

import { colors } from 'global/colors';
import { monokaiBackground, darkModeBackground } from 'global/themeDark';

export const CommentContainerSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  & :is(h1, h2, h3, h4, h5, h6) {
    color: ${({ theme }) => theme.header};
    font-weight: 500;
  }

  & p,
  & label,
  & span {
    color: ${({ theme }) => theme.text};
  }

  ${({ isDarkMode }) =>
    isDarkMode &&
    css`
      background: ${monokaiBackground};
      color: ${colors.lightGray};

      & .darkModeSupport {
        background: ${darkModeBackground};

        & a {
          color: ${colors.fsCyan};
        }

        & .ql-syntax {
          /* Code block pre tag */
          background: ${monokaiBackground};
          color: ${colors.white};
        }

        & button:hover {
          background: ${monokaiBackground};

          & :is(.fa-comment-o, .fa-comments-o) {
            color: ${colors.white};
          }
        }
      }

      & .quill {
        background: ${darkModeBackground};
        color: ${colors.lightGray} !important;

        & .ql-editor {
          color: ${colors.lightGray} !important;
          & .ql-syntax {
            background: ${monokaiBackground};
            color: ${colors.white};
          }
          & a {
            color: ${colors.fsCyan};
          }
        }

        & .ql-toolbar {
          & .ql-stroke {
            stroke: ${colors.gray};
          }
          & .ql-fill {
            fill: ${colors.gray};
          }

          & button.ql-active,
          & button:hover {
            & .ql-stroke {
              stroke: ${colors.fsCyan};
            }
            & .ql-fill {
              fill: ${colors.fsCyan};
            }
          }
        }
      }
    `}
`;

export const ContainerRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const ContainerColumn = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const EditorContainer = styled.div`
  width: 100%;
  padding-bottom: 5px;
`;

export const QuillContainer = styled.div`
  background: white;
  margin-bottom: 5px;
`;

export const CommentContainer = styled.div.attrs({ className: 'darkModeSupport' })`
  width: 100%;
  background: white;
  border-radius: 1rem 5px 5px 5px;
  margin-bottom: 5px;
  display: flex;
  flex-direction: row;
  ${({ isRootComment }) => {
    return css`
      padding: 5px ${isRootComment ? '5px' : '0px'} 0px 5px;
    `;
  }}
`;

export const CommentTextContainer = styled.div`
  font-family: Helvetica, Arial, 'Open Sans', sans-serif;
  font-size: 1em;
  line-height: 2;

  & :is(h2, h3, h4, h5, h6) {
    font-size: 1.5em;
  }
  & h1 {
    font-size: 2em;
  }
  & p {
    margin: 0px;
  }
`;

export const CommentThumbnailColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  user-select: none;
`;

export const CommentThumbnail = styled.div`
  border-radius: 50%;
  min-width: 2rem;
  min-height: 2rem;
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  background-image: url(${({ accountThumbnail }) => accountThumbnail || ''});
`;

export const ThumbnailLineBox = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: 100%;
`;

export const ThumbnailLine = styled.div`
  height: 100%;
  background: lightgray;
  width: 1px;
`;

export const ThumbnailLineSpacer = styled.div`
  height: 1rem;
`;

export const CommentColumn = styled.div`
  margin-left: 10px;
  width: 100%;
`;

export const CommentPoster = styled.a`
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  color: ${colors.fsDarkBlue};

  &:hover {
    color: ${colors.fsDarkBlueHoverLight};
    text-decoration: underline;
  }
`;

export const DateText = styled.label`
  font-size: 0.8rem;
  color: ${colors.fsDarkBlueHoverLight};
`;

export const AlignBox = styled.div`
  // align-box to use when content divided left and right
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const AlignBoxLeft = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  gap: 5px;
`;

export const AlignBoxRight = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: right;
  gap: 5px;
`;

export const FooterAlignBox = styled(AlignBox)`
  user-select: none;
  margin-bottom: 2px;
`;

export const FooterButtonContainer = styled.button`
  padding: 0px 2px;
  border: 0;
  line-height: 0;
  height: 1.2rem;
  background: transparent;
  color: gray;
  &:hover {
    border-radius: 5px;
    ${({ hoverColor, hoverBackgroundColor }) => {
      return css`
        color: ${hoverColor || 'black'};
        background: ${hoverBackgroundColor || 'lightgray'};
      `;
    }}
  }
`;

export const FooterButtonIcon = styled.i.attrs(({ icon }) => ({
  // icon is a required prop
  className: icon,
}))`
  margin-right: 5px;
  font-size: 1rem;
`;

export const FooterButtonLabel = styled.label`
  font-size: 0.8rem;
  cursor: pointer;
  margin: 0;
`;
